// Extend the MutationObserver interface to include the feJsEvent property
interface JsEventMutationObserver extends MutationObserver {
  feJsEvent?: JsEvent;
}

// Extend the HTMLElement interface to include the feJsEvent property
interface JsEventHTMLElement extends HTMLElement {
  feJsEvent?: JsEvent;
}

/**
 * Factory class for creating and managing JsEvent instances.
 */
export default class JsEventFactory {
  /**
   * Connects a .NET reference to a DOM element and initializes a JsEvent instance.
   * @param {any} dotNetRef - The .NET reference.
   * @param {string} elementId - The ID of the DOM element.
   * @param {object} options - Configuration options for the JsEvent.
   * @throws Will throw an error if elementId is not provided or if the element is not found.
   */
  connect(dotNetRef: any, elementId: string, options: object): void {
    if (!elementId)
      throw new Error("[FeBlazorLibrary | JsEvent] elementId: expected element id!");

    const element: JsEventHTMLElement | null = document.getElementById(elementId);
    if (!element)
      throw new Error("[FeBlazorLibrary | JsEvent] no element found for id: " + elementId);

    if (!element.feJsEvent)
      element.feJsEvent = new JsEvent(dotNetRef, options);

    element.feJsEvent.connect(element);
  }

  /**
   * Disconnects the JsEvent instance from the DOM element.
   * @param {string} elementId - The ID of the DOM element.
   */
  disconnect(elementId: string): void {
    const element: JsEventHTMLElement | null = document.getElementById(elementId);
    if (!element || !element.feJsEvent)
      return;

    element.feJsEvent.disconnect();
  }

  /**
   * Subscribes to a specific event on the DOM element.
   * @param {string} elementId - The ID of the DOM element.
   * @param {string} eventName - The name of the event to subscribe to.
   * @throws Will throw an error if elementId is not provided or if the element is not found.
   */
  subscribe(elementId: string, eventName: string): void {
    if (!elementId)
      throw new Error("[FeBlazorLibrary | JsEvent] elementId: expected element id!");

    const element: JsEventHTMLElement | null = document.getElementById(elementId);
    if (!element)
      throw new Error("[FeBlazorLibrary | JsEvent] no element found for id: " + elementId);

    if (!element.feJsEvent)
      throw new Error("[FeBlazorLibrary | JsEvent] please connect before subscribing");

    element.feJsEvent.subscribe(eventName);
  }

  /**
   * Unsubscribes from a specific event on the DOM element.
   * @param {string} elementId - The ID of the DOM element.
   * @param {string} eventName - The name of the event to unsubscribe from.
   */
  unsubscribe(elementId: string, eventName: string): void {
    const element: JsEventHTMLElement | null = document.getElementById(elementId);
    if (!element || !element.feJsEvent)
      return;

    element.feJsEvent.unsubscribe(eventName);
  }
}

/**
 * Class representing a JavaScript event handler for FeBlazorLibrary.
 */
class JsEvent {
  private _dotNetRef: any;
  private _options: any;
  private logger: (message?: any, ...optionalParams: any[]) => void;
  private _subscribedEvents: { [key: string]: boolean };
  private _observer: JsEventMutationObserver | null;
  private _element: HTMLElement | null;
  private _observedChildren: HTMLElement[];

  // Add an index signature to allow dynamic property access
  [key: string]: any;

  /**
   * Creates an instance of JsEvent.
   * @param {any} dotNetRef - The .NET reference.
   * @param {object} options - Configuration options.
   */
  constructor(dotNetRef: any, options: any) {
    this._dotNetRef = dotNetRef;
    this._options = options || {};
    this.logger = options.enableLogging ? console.log : () => { };
    this.logger('[FeBlazorLibrary | JsEvent] Initialized', { options });
    this._subscribedEvents = {};
    this._observer = null;
    this._element = null;
    this._observedChildren = [];
  }

  /**
   * Connects the JsEvent instance to a DOM element.
   * @param {HTMLElement} element - The DOM element to connect to.
   * @throws Will throw an error if targetClass is not provided in options.
   */
  connect(element: HTMLElement): void {
    if (!this._options)
      return;

    if (!this._options.targetClass)
      throw new Error("_options.targetClass: css class name expected");

    if (this._observer) {
      // don't do double registration
      return;
    }
    const targetClass: string = this._options.targetClass;
    this.logger('[FeBlazorLibrary | JsEvent] Start observing DOM of element for changes to child with class ', { element, targetClass });
    this._element = element;
    this._observer = new MutationObserver(this.onDomChanged);
    this._observer.feJsEvent = this;
    this._observer.observe(this._element, { attributes: false, childList: true, subtree: true });
    this._observedChildren = [];
  }

  /**
   * Disconnects the JsEvent instance from the DOM element.
   */
  disconnect(): void {
    if (!this._observer)
      return;

    this.logger('[FeBlazorLibrary | JsEvent] disconnect mutation observer and event handler ');
    this._observer.disconnect();
    this._observer = null;
    for (const child of this._observedChildren)
      this.detachHandlers(child);
  }

  /**
   * Subscribes to a specific event on the DOM element.
   * @param {string} eventName - The name of the event to subscribe to.
   */
  subscribe(eventName: string): void {
    if (this._subscribedEvents[eventName]) {
      //console.log("... already attached");
      return;
    }
    const element: HTMLElement = this._element!;
    const targetClass: string = this._options.targetClass;
    this._subscribedEvents[eventName] = true;
    for (const child of element.getElementsByClassName(targetClass)) {
      this.attachHandlers(child as HTMLElement);
    }
  }

  /**
   * Unsubscribes from a specific event on the DOM element.
   * @param {string} eventName - The name of the event to unsubscribe from.
   */
  unsubscribe(eventName: string): void {
    if (!this._observer)
      return;

    this.logger('[FeBlazorLibrary | JsEvent] unsubscribe event handler ' + eventName);
    this._observer.disconnect();
    this._observer = null;
    this._subscribedEvents[eventName] = false;
    for (const child of this._observedChildren) {
      this.detachHandler(child, eventName);
    }
  }

  /**
   * Attaches event handlers to a child element.
   * @param {HTMLElement} child - The child element to attach handlers to.
   */
  attachHandlers(child: JsEventHTMLElement): void {
    child.feJsEvent = this;
    for (const eventName of Object.keys(this._subscribedEvents)) {
      if (!this._subscribedEvents[eventName])
        continue;

      this.logger('[FeBlazorLibrary | JsEvent] attaching event ' + eventName, child);
      child.addEventListener(eventName, this.eventHandler);
    }
    if (this._observedChildren.indexOf(child) < 0)
      this._observedChildren.push(child);
  }

  /**
   * Detaches a specific event handler from a child element.
   * @param {HTMLElement} child - The child element to detach the handler from.
   * @param {string} eventName - The name of the event to detach.
   */
  detachHandler(child: HTMLElement, eventName: string): void {
    this.logger('[FeBlazorLibrary | JsEvent] detaching handler ' + eventName, child);
    child.removeEventListener(eventName, this.eventHandler);
  }

  /**
   * Detaches all event handlers from a child element.
   * @param {HTMLElement} child - The child element to detach handlers from.
   */
  detachHandlers(child: HTMLElement): void {
    this.logger('[FeBlazorLibrary | JsEvent] detaching handlers ', child);
    for (const eventName of Object.keys(this._subscribedEvents)) {
      if (!this._subscribedEvents[eventName])
        continue;

      child.removeEventListener(eventName, this.eventHandler);
    }
    this._observedChildren = this._observedChildren.filter(x => x !== child);
  }

  /**
   * Handles DOM changes and attaches/detaches event handlers as needed.
   * @param {MutationRecord[]} mutationsList - List of mutations observed.
   * @param {MutationObserver} observer - The observer instance.
   */
  onDomChanged(mutationsList: MutationRecord[], observer: MutationObserver): void {
    const self: JsEvent = this.feJsEvent; // func is invoked with this == _observer
    const targetClass: string = self._options.targetClass;
    for (const mutation of mutationsList) {
      for (const element of mutation.addedNodes) {
        if ((element as Element).classList && (element as Element).classList.contains(targetClass)) {
          if (!self._options.TagName || (element as Element).tagName === self._options.TagName)
            self.attachHandlers(element as HTMLElement);
        }
      }
      for (const element of mutation.removedNodes) {
        if ((element as Element).classList && (element as Element).classList.contains(targetClass)) {
          if (!self._options.tagName || (element as Element).tagName === self._options.tagName)
            self.detachHandlers(element as HTMLElement);
        }
      }
    }
  }

  /**
   * General event handler that delegates to specific event handlers.
   * @param {Event} e - The event object.
   */
  eventHandler(e: Event): void {
    const self: JsEvent = this.feJsEvent; // func is invoked with this == child
    const eventName: string = e.type;
    self.logger('[FeBlazorLibrary | JsEvent] "' + eventName + '"', e);
    self["on" + eventName](self, e);
  }

  /**
   * Handles the 'keyup' event.
   * @param {JsEvent} self - The JsEvent instance.
   * @param {KeyboardEvent} e - The keyboard event object.
   */
  onkeyup(self: JsEvent, e: KeyboardEvent): void {
    const caretPosition: number | null = (e.target as HTMLInputElement).selectionStart;
    const invoke: boolean = self._subscribedEvents["keyup"];
    if (invoke) {
      self._dotNetRef.invokeMethodAsync('OnCaretPositionChanged', caretPosition);
    }
  }

  /**
   * Handles the 'click' event.
   * @param {JsEvent} self - The JsEvent instance.
   * @param {MouseEvent} e - The mouse event object.
   */
  onclick(self: JsEvent, e: MouseEvent): void {
    const caretPosition: number | null = (e.target as HTMLInputElement).selectionStart;
    const invoke: boolean = self._subscribedEvents["click"];
    if (invoke) {
      self._dotNetRef.invokeMethodAsync('OnCaretPositionChanged', caretPosition);
    }
  }

  /**
   * Handles the 'paste' event.
   * @param {JsEvent} self - The JsEvent instance.
   * @param {ClipboardEvent} e - The clipboard event object.
   */
  onpaste(self: JsEvent, e: ClipboardEvent): void {
    const invoke: boolean = self._subscribedEvents["paste"];
    if (invoke) {
      e.preventDefault();
      e.stopPropagation();
      const clipboardData: DataTransfer | null = e.clipboardData;
      if (!clipboardData) {
        self.logger('[FeBlazorLibrary | JsEvent] clipboardData is null', e);
        return;
      }
      const text: string = clipboardData.getData('text/plain');
      self._dotNetRef.invokeMethodAsync('OnPaste', text);
    }
  }

  /**
   * Handles the 'select' event.
   * @param {JsEvent} self - The JsEvent instance.
   * @param {Event} e - The event object.
   */
  onselect(self: JsEvent, e: Event): void {
    const invoke: boolean = self._subscribedEvents["select"];
    if (invoke) {
      const start: number | null = (e.target as HTMLInputElement).selectionStart;
      const end: number | null = (e.target as HTMLInputElement).selectionEnd;
      if (start === end)
        return; // <-- we have caret position changed for that.

      self._dotNetRef.invokeMethodAsync('OnSelect', start, end);
    }
  }
}
